import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getPlatformTenants } from "api/platformApi";
import { unwrapList } from "api/unwrap";

/**
 * /platform/tenants — cross-tenant rollup for the platform owner. One
 * row per whitelabel tenant: drivers, brokers, compliance %, packets
 * sent (30d), plan/status, last audit activity.
 *
 * Backend gates this behind platform.admin.enabled + SUPER_ADMIN —
 * returns [] when disabled, in which case we render an explanatory
 * empty state instead of an error.
 */

const Kpi = ({ label, value, hint, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent || "#0f172a", mt: 1 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{hint}</Typography>
      ) : null}
    </CardContent>
  </Card>
);

const fmtDate = (ms) => {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString();
};

const fmtActivity = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
};

const pctColor = (p) =>
  p == null ? "#94a3b8" :
  p >= 90 ? "#16a34a" :
  p >= 70 ? "#f97316" : "#dc2626";

export const PlatformTenantsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformTenants();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.name || ""} ${r.subdomain || ""} ${r.plan || ""} ${r.status || ""}`.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = {
      tenants: rows.length,
      drivers: 0,
      brokers: 0,
      packets30d: 0,
      active: 0,
    };
    rows.forEach((r) => {
      t.drivers += r.totalDrivers || 0;
      t.brokers += r.totalBrokers || 0;
      t.packets30d += r.packetsSent30d || 0;
      if (r.status === "active") t.active += 1;
    });
    return t;
  }, [rows]);

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Platform — Tenants
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every whitelabel tenant's size, compliance posture, and recent audit activity.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No tenants returned. The platform admin endpoint is gated behind a server-side feature flag
              ({`platform.admin.enabled=true`}) and SUPER_ADMIN authority. Set the flag in SSM and reload.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Kpi label="Tenants" value={totals.tenants} hint={`${totals.active} active`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Total drivers" value={totals.drivers} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Total brokers" value={totals.brokers} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Packets (30d)" value={totals.packets30d} hint="Across all tenants" />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search by name / subdomain / plan / status"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 280 }}
            />
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plan / Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Drivers</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Brokers</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Compliance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Auto-email</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Packets (30d)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last audit</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.tenantId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.subdomain || ""}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.plan || "—"} sx={{ height: 20, mr: 0.5 }} />
                      <Chip
                        size="small"
                        label={r.status || "—"}
                        sx={{
                          height: 20,
                          backgroundColor: r.status === "active" ? "#dcfce7" : "#fee2e2",
                          color: r.status === "active" ? "#166534" : "#991b1b",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{r.totalDrivers ?? 0}</TableCell>
                    <TableCell align="right">{r.totalBrokers ?? 0}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={`${r.overallPercent ?? 0}%`}
                        sx={{
                          fontWeight: 700,
                          backgroundColor: pctColor(r.overallPercent),
                          color: "#fff",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{r.brokersOnAutoEmail ?? 0}</TableCell>
                    <TableCell align="right">{r.packetsSent30d ?? 0}</TableCell>
                    <TableCell>{fmtActivity(r.lastAuditActivityMs)}</TableCell>
                    <TableCell>{fmtDate(r.createdAtMs)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

export default PlatformTenantsPage;
